import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Checkbox, Dropdown, Input, MessageBarBody, MessageBarTitle, Option, Switch, Textarea } from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';

// models
import { JuridictionIdentifierTypeEnum } from '../../../models/Entities/Person/IdentifierType/IdentifierTypeModels';

// components
import { GuideDrawer, GuideDrawerForm } from '../../../theme/components/GuideDrawer';
import { GuideField, GuideFieldGrid } from '../../../theme/components/GuideField';
import { GuideMessageBar } from '../../../theme/components/GuideMessageBar';

// functions
import * as CommonFunctions from '../Persons.Functions';
import * as ownFunctions from './IdentifierType.Form.Functions';
import {
  IDENTIFIER_TYPE_DESCRIPTION_MAX_LENGTH,
  IDENTIFIER_TYPE_MASK_MAX_LENGTH,
  IDENTIFIER_TYPE_NAME_MAX_LENGTH,
  IDENTIFIER_TYPE_REGEX_MAX_LENGTH,
  IdentifierTypeFormValues,
  IdentifierTypeServerErrors,
  IdentifierTypeServerField,
  IdentifierTypeValidation,
  formValuesOf,
  hasErrors,
  identifierTypeErrorKind,
  identifierTypeErrorMessage,
  serverErrorsByField,
  toCommand,
  validateIdentifierType
} from './IdentifierTypes.Functions';
import './IdentifierTypes.css';

/**
 * Drawer de alta / modificación de un tipo de identificador (spec 011). Valida en el cliente como ayuda
 * inmediata (nombre obligatorio, ≤ 100 y no repetido; expresión regular que compile; física o jurídica); el
 * servidor (Persons.API spec 005) es la garantía: 409 nombre repetido → su mensaje en el campo; 400 → cada
 * `errors[]` en su campo; 404 al modificar → recarga la lista y avisa (`onNotFound`). "Crear CUIT" lo abre
 * con `prefill`.
 */
const IdentifierTypeFormComponent = (props: ownFunctions.Props) => {
  const [t] = useTranslation();
  const tf = (key: string, options?: Record<string, unknown>) => String(t(`persons.identifierTypes.form.${key}`, options));

  const [values, setValues] = useState<IdentifierTypeFormValues>(() => formValuesOf(props.identifierType, props.prefill));
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [saveError, setSaveError] = useState<unknown>(undefined);
  const [serverErrors, setServerErrors] = useState<IdentifierTypeServerErrors['fields']>({});

  useEffect(() => {
    if (!props.isOpen) return;
    setValues(formValuesOf(props.identifierType, props.prefill));
    setSubmitted(false);
    setSaveError(undefined);
    setServerErrors({});
    // Sólo al abrir o cambiar de tipo: la recarga de la lista no pisa lo que se está escribiendo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isOpen, props.identifierTypeId, props.prefill]);

  const serverFieldOf = (key: keyof IdentifierTypeFormValues): IdentifierTypeServerField | undefined => {
    if (key === 'isForNaturalPerson' || key === 'isForJuridicalPerson') return 'personType';
    if (key === 'isActive') return undefined;
    return key;
  };

  const set = <K extends keyof IdentifierTypeFormValues>(key: K, value: IdentifierTypeFormValues[K]) => {
    setValues((current) => ({ ...current, [key]: value }));
    // El error del servidor de ese campo deja de valer cuando se lo modifica.
    const field = serverFieldOf(key);
    if (field && serverErrors[field]) {
      setServerErrors((current) => {
        const next = { ...current };
        delete next[field];
        return next;
      });
    }
  };

  const validation: IdentifierTypeValidation = validateIdentifierType(
    values,
    props.existingTypes ?? [],
    props.identifierType?.id
  );
  const showErrors = submitted && hasErrors(validation);

  const nameError = (() => {
    if (!submitted || !validation.name) return serverErrors.name;
    if (validation.name === 'tooLong') return tf('validation.nameLength', { max: IDENTIFIER_TYPE_NAME_MAX_LENGTH });
    if (validation.name === 'duplicate') return tf('validation.nameDuplicate');
    return tf('validation.name');
  })();
  const personTypeError = submitted && validation.personType ? tf('validation.personType') : serverErrors.personType;
  const regexError = (() => {
    if (!submitted || !validation.validationRegex) return serverErrors.validationRegex;
    if (validation.validationRegex === 'tooLong') return tf('validation.regexLength', { max: IDENTIFIER_TYPE_REGEX_MAX_LENGTH });
    return tf('validation.regex');
  })();
  const descriptionError =
    submitted && validation.description
      ? tf('validation.descriptionLength', { max: IDENTIFIER_TYPE_DESCRIPTION_MAX_LENGTH })
      : serverErrors.description;
  const maskError =
    submitted && validation.identifierMask
      ? tf('validation.maskLength', { max: IDENTIFIER_TYPE_MASK_MAX_LENGTH })
      : serverErrors.identifierMask;
  const juridictionError = serverErrors.juridictionType;
  const errorList = [nameError, descriptionError, personTypeError, maskError, regexError].filter((error): error is string => !!error);

  const isSaving = saving || !!props.isAdding || !!props.isUpdating;

  const handleSave = async () => {
    if (isSaving) return;
    setSubmitted(true);
    setSaveError(undefined);
    setServerErrors({});
    if (hasErrors(validation)) return;

    const payload = toCommand(values);
    setSaving(true);
    try {
      const editing = props.identifierType;
      const saved = editing
        ? await props.UpdateIdentifierType({ ...payload, id: editing.id })
        : await props.CreateIdentifierType(payload);
      props.GetIdentifierTypes();
      props.onSaved?.(saved?.name || payload.name || '', editing ? 'updated' : 'created');
      props.dismissPanel();
    } catch (error: unknown) {
      handleSaveError(error, payload.name || '');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveError = (error: unknown, name: string) => {
    const kind = identifierTypeErrorKind(error);
    if (kind === 'notFound' && props.identifierType) {
      // Otro usuario lo eliminó: se recarga la lista y la pantalla avisa.
      props.GetIdentifierTypes();
      if (props.onNotFound) {
        props.onNotFound(props.identifierType.name || name);
        props.dismissPanel();
        return;
      }
      setSaveError({ ServerMessage: tf('notFound') });
      return;
    }
    if (kind === 'duplicate') {
      // El existente puede no estar en la lista cargada: se recarga para que la validación local lo vea.
      props.GetIdentifierTypes();
      setServerErrors({ name: identifierTypeErrorMessage(error) });
    }
    if (kind === 'validation') {
      setServerErrors(serverErrorsByField((error as { Errors?: string[] }).Errors).fields);
    }
    setSaveError(error);
  };

  const selectedJuridiction = CommonFunctions.JuridictionTypeOptions.find((option) => option.key === values.juridictionType);
  const juridictionText = (option: { textKey: string; defaultText: string }): string =>
    String(t(option.textKey, { defaultValue: option.defaultText }));

  return (
    <GuideDrawer
      open={props.isOpen}
      size="medium"
      title={
        props.identifierType
          ? t('persons.identifierTypes.form.editTitle', { defaultValue: 'Editar tipo de identificación' })
          : t('persons.identifierTypes.form.addTitle', { defaultValue: 'Nuevo tipo de identificación' })
      }
      onDismiss={props.dismissPanel}
      preventDismiss={isSaving}
      testId="identifier-type-drawer"
      secondaryAction={{ label: t('persons.form.footer.cancel', { defaultValue: 'Cancelar' }) }}
      primaryAction={{
        label: t('persons.form.footer.save', { defaultValue: 'Guardar' }),
        onClick: handleSave,
        loading: isSaving,
        testId: 'identifier-type-save'
      }}
    >
      {showErrors && (
        <GuideMessageBar intent="error" layout="multiline" data-testid="identifier-type-validation">
          <MessageBarBody>
            <MessageBarTitle>{t('persons.form.review', { defaultValue: 'Revise la información ingresada' })}</MessageBarTitle>
            <ul>
              {errorList.map((error) => (
                <li key={error}>{error}</li>
              ))}
            </ul>
          </MessageBarBody>
        </GuideMessageBar>
      )}

      {saveError !== undefined && (
        <GuideMessageBar intent="error" layout="multiline" data-testid="identifier-type-save-error">
          <MessageBarBody>
            <MessageBarTitle>{tf('saveError')}</MessageBarTitle>
            {identifierTypeErrorMessage(saveError)}
          </MessageBarBody>
        </GuideMessageBar>
      )}

      <GuideDrawerForm onSubmit={handleSave} testId="identifier-type-form">
        <GuideField
          label={t('persons.identifierTypes.form.name', { defaultValue: 'Nombre' })}
          required
          hint={tf('nameHint')}
          error={nameError}
          counter={{ value: values.name.trim().length, max: IDENTIFIER_TYPE_NAME_MAX_LENGTH }}
        >
          <Input value={values.name} onChange={(_, data) => set('name', data.value || '')} data-testid="identifier-type-name" />
        </GuideField>

        <GuideField
          label={t('persons.identifierTypes.form.description', { defaultValue: 'Descripción' })}
          error={descriptionError}
          counter={{ value: values.description.trim().length, max: IDENTIFIER_TYPE_DESCRIPTION_MAX_LENGTH }}
        >
          <Textarea
            value={values.description}
            onChange={(_, data) => set('description', data.value || '')}
            data-testid="identifier-type-description"
          />
        </GuideField>

        <GuideField label={t('persons.identifierTypes.form.juridictionType', { defaultValue: 'Jurisdicción' })} error={juridictionError}>
          <Dropdown
            value={selectedJuridiction ? juridictionText(selectedJuridiction) : ''}
            selectedOptions={[String(values.juridictionType)]}
            onOptionSelect={(_, data) => set('juridictionType', Number(data.optionValue) as JuridictionIdentifierTypeEnum)}
          >
            {CommonFunctions.JuridictionTypeOptions.map((option) => (
              <Option key={option.key} value={String(option.key)} text={juridictionText(option)}>
                {juridictionText(option)}
              </Option>
            ))}
          </Dropdown>
        </GuideField>

        <fieldset
          className={personTypeError ? 'idt-fieldset idt-fieldset--error' : 'idt-fieldset'}
          aria-invalid={personTypeError ? true : undefined}
          aria-describedby={personTypeError ? 'identifier-type-person-type-error' : undefined}
          data-testid="identifier-type-applies-to"
        >
          <legend className="idt-legend">{tf('appliesTo')}</legend>
          <Checkbox
            label={t('persons.identifierTypes.form.isForNaturalPerson', { defaultValue: 'Aplica a personas físicas' })}
            checked={values.isForNaturalPerson}
            onChange={(_, data) => set('isForNaturalPerson', !!data.checked)}
          />
          <Checkbox
            label={t('persons.identifierTypes.form.isForJuridicalPerson', { defaultValue: 'Aplica a personas jurídicas' })}
            checked={values.isForJuridicalPerson}
            onChange={(_, data) => set('isForJuridicalPerson', !!data.checked)}
          />
          {personTypeError && (
            <span id="identifier-type-person-type-error" className="idt-error">
              {personTypeError}
            </span>
          )}
        </fieldset>

        <GuideFieldGrid>
          <GuideField
            label={t('persons.identifierTypes.form.identifierMask', { defaultValue: 'Máscara' })}
            hint={t('persons.identifierTypes.form.identifierMaskHint', { defaultValue: 'Ejemplo: ##-########-#' })}
            error={maskError}
            counter={{ value: values.identifierMask.trim().length, max: IDENTIFIER_TYPE_MASK_MAX_LENGTH }}
          >
            <Input
              value={values.identifierMask}
              onChange={(_, data) => set('identifierMask', data.value || '')}
              data-testid="identifier-type-mask"
            />
          </GuideField>

          <GuideField
            label={t('persons.identifierTypes.form.validationRegex', { defaultValue: 'Expresión regular de validación' })}
            hint={tf('validationRegexHint')}
            error={regexError}
            counter={{ value: values.validationRegex.trim().length, max: IDENTIFIER_TYPE_REGEX_MAX_LENGTH }}
          >
            <Input
              value={values.validationRegex}
              onChange={(_, data) => set('validationRegex', data.value || '')}
              className="idt-mono"
              data-testid="identifier-type-regex"
            />
          </GuideField>
        </GuideFieldGrid>

        <Switch
          label={t('persons.identifierTypes.form.isActive', { defaultValue: 'Activo' })}
          checked={values.isActive}
          onChange={(_, data) => set('isActive', !!data.checked)}
        />
      </GuideDrawerForm>
    </GuideDrawer>
  );
};

export default connect(ownFunctions.mapStateToProps, ownFunctions.mapDispatchToProps)(IdentifierTypeFormComponent as any);
